<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm font-semibold text-gray-700">
        Real-time health status of integrated external services.
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="fetch">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div class="relative min-h-[320px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading service health...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Service</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Failures</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Latency</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Last success</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Last failure</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Last checked</th>
                  <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 max-w-[14rem]">Last error</th>
                  <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-28">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in services"
                  :key="row.serviceName"
                  class="hover:bg-gray-50 transition"
                >
                  <td class="py-3 px-4 border-b border-gray-100">{{ idx + 1 }}</td>
                  <td class="py-3 px-4 border-b border-gray-100 font-semibold text-gray-800">
                    {{ row.serviceName }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100">
                    <span
                      class="px-2 py-0.5 rounded-full text-xs font-semibold"
                      :class="healthStatusBadgeClass(row.status)"
                    >
                      {{ row.status || "—" }}
                    </span>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100">{{ row.consecutiveFailures }}</td>
                  <td class="py-3 px-4 border-b border-gray-100 whitespace-nowrap">
                    {{ row.lastLatencyMs != null ? `${row.lastLatencyMs} ms` : "—" }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 whitespace-nowrap">
                    {{ row.lastSuccessAt || "—" }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 whitespace-nowrap">
                    {{ row.lastFailureAt || "—" }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 whitespace-nowrap">
                    {{ row.lastCheckedAt || "—" }}
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 max-w-[14rem]">
                    <p class="truncate" :title="row.lastError || undefined">{{ row.lastError || "—" }}</p>
                  </td>
                  <td class="py-3 px-4 border-b border-gray-100 text-center">
                    <button
                      type="button"
                      class="btn-sm-primary whitespace-nowrap"
                      :disabled="detailLoadingId === row.serviceName"
                      @click="viewDetails(row.serviceName)"
                    >
                      <i
                        class="fa-solid mr-1"
                        :class="detailLoadingId === row.serviceName ? 'fa-spinner fa-spin' : 'fa-eye'"
                      ></i>
                      View
                    </button>
                  </td>
                </tr>
                <tr v-if="loaded && !loading && services.length === 0">
                  <td colspan="10" class="py-8 px-4 text-center text-gray-500">
                    No service health data found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <AppModal v-model="showDetail" title="Service Health Details" size="lg" hide-footer>
      <div v-if="selectedService" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
          <p><span class="font-semibold text-gray-500 mr-2">Service:</span>{{ selectedService.serviceName }}</p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Status:</span>
            <span
              class="px-2 py-0.5 rounded-full text-xs font-semibold"
              :class="healthStatusBadgeClass(selectedService.status)"
            >
              {{ selectedService.status }}
            </span>
          </p>
          <p><span class="font-semibold text-gray-500 mr-2">Consecutive failures:</span>{{ selectedService.consecutiveFailures }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Latency:</span>{{ selectedService.lastLatencyMs != null ? `${selectedService.lastLatencyMs} ms` : "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Last success:</span>{{ selectedService.lastSuccessAt || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Last failure:</span>{{ selectedService.lastFailureAt || "—" }}</p>
          <p><span class="font-semibold text-gray-500 mr-2">Last checked:</span>{{ selectedService.lastCheckedAt || "—" }}</p>
          <p class="md:col-span-2 break-all">
            <span class="font-semibold text-gray-500 mr-2">Last error:</span>{{ selectedService.lastError || "—" }}
          </p>
        </div>

        <div>
          <h4 class="font-semibold text-gray-700 mb-2">Status history (last 25)</h4>
          <div class="bg-white rounded border border-gray-200 overflow-hidden">
            <table class="w-full text-xs">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left py-2 px-3 font-semibold text-gray-700 border-b">When</th>
                  <th class="text-left py-2 px-3 font-semibold text-gray-700 border-b">From</th>
                  <th class="text-left py-2 px-3 font-semibold text-gray-700 border-b">To</th>
                  <th class="text-left py-2 px-3 font-semibold text-gray-700 border-b">Reason</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(event, idx) in selectedService.statusHistory"
                  :key="`${event.occurredAt}-${idx}`"
                  class="hover:bg-gray-50"
                >
                  <td class="py-2 px-3 border-b border-gray-100 whitespace-nowrap">{{ event.occurredAt || "—" }}</td>
                  <td class="py-2 px-3 border-b border-gray-100">
                    <span class="px-2 py-0.5 rounded-full text-xs font-semibold" :class="healthStatusBadgeClass(event.previousStatus)">
                      {{ event.previousStatus }}
                    </span>
                  </td>
                  <td class="py-2 px-3 border-b border-gray-100">
                    <span class="px-2 py-0.5 rounded-full text-xs font-semibold" :class="healthStatusBadgeClass(event.status)">
                      {{ event.status }}
                    </span>
                  </td>
                  <td class="py-2 px-3 border-b border-gray-100 break-all">{{ event.reason || "—" }}</td>
                </tr>
                <tr v-if="selectedService.statusHistory.length === 0">
                  <td colspan="4" class="py-6 text-center text-gray-400">No status transitions recorded.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast } from "@/composables";
import {
  extractErrorMessage,
  fetchAllServicesHealth,
  fetchServiceHealth,
  healthStatusBadgeClass,
  type ServiceHealth,
} from "../monitoringApi";

const { errorToast } = useToast();

const loading = ref(false);
const loaded = ref(false);
const services = ref<ServiceHealth[]>([]);
const showDetail = ref(false);
const selectedService = ref<ServiceHealth | null>(null);
const detailLoadingId = ref<string | null>(null);

const fetch = async () => {
  loading.value = true;
  try {
    services.value = await fetchAllServicesHealth();
  } catch (error: unknown) {
    services.value = [];
    errorToast("Error", extractErrorMessage(error, "Could not load service health"));
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

const viewDetails = async (serviceName: string) => {
  detailLoadingId.value = serviceName;
  try {
    selectedService.value = await fetchServiceHealth(serviceName);
    showDetail.value = true;
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not load service health details"));
  } finally {
    detailLoadingId.value = null;
  }
};

onMounted(() => fetch());

defineExpose({ refresh: fetch });
</script>
